/* eslint-disable @typescript-eslint/no-empty-function */
// TODO: move this css boilerplate into a next.js plugin
import 'normalize.css'; // css reset
import '@cvent/carina/fonts/fonts.web.css';

import React, { useMemo } from 'react';
import { ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ApolloClientFactory, useApollo } from '@cvent/apollo-client';
import { initRum } from '@cvent/nextjs/datadog/initRum';
import { TRACKING_ID } from '@cvent/universal-login-web-middleware';

import { datadogOptions } from '@configs/initBrowserLogging';
import { getCookie } from '@utils/RequestHelpers';
import { AppProviders } from '@components/framework/AppProviders';
import { ErrorBoundary } from '@components/framework/ErrorBoundary';
import { clientVersion, localeVar, darkModeVar } from '@components/framework/ReactiveVars';
import { Layout } from '@components/framework/Layout';

/**
 * Ensure apollo operations get performed against our
 * nextjs hosted GraphQL route.
 */
const httpLink = new HttpLink({
  uri: '/api/graphql'
});

/**
 * Embed cookies into apollo context for user tracking.
 */
const trackingLink = setContext((_, { headers }) => {
  return {
    headers: new Headers({
      ...headers,
      cookie: headers?.cookie ?? document.cookie ?? '',
      credentials: 'include'
    })
  };
});

/**
 * Handle Client-Side Apollo errors by redirecting user to error page.
 */
const errorLink = onError(({ graphQLErrors, operation }) => {
  if (graphQLErrors) {
    if (typeof window !== 'undefined') {
      const requestTrackingId = getCookie(TRACKING_ID, operation.getContext().headers.cookie);

      window.location.href = `/_error?${TRACKING_ID}=${requestTrackingId}`;
    }
  }
});

export default function App({ Component, pageProps }): JSX.Element {
  initRum(datadogOptions);

  const client = useApollo(
    pageProps,
    new ApolloClientFactory({
      initialState: {},
      loginUrl: '',
      graphBasePath: '',
      graphUri: ''
    }),
    {
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              clientVersion: {
                read(): string {
                  return clientVersion();
                }
              },
              darkMode: {
                read(): boolean {
                  return darkModeVar();
                }
              },
              locale: {
                read(): string {
                  return localeVar();
                }
              }
            }
          }
        }
      }),
      link: trackingLink.concat(errorLink).concat(httpLink)
    }
  );

  return useMemo(() => {
    return (
      <ApolloProvider client={client}>
        <AppProviders>
          <ErrorBoundary>
            <Layout>
              <Component {...pageProps} />
            </Layout>
          </ErrorBoundary>
        </AppProviders>
      </ApolloProvider>
    );
  }, [client, pageProps, Component]);
}
